import { Download, Phone, RefreshCw } from 'lucide-react';
import { fmtPhone } from '@siva/shared';
import type { VersionState } from '../../lib/app-version.js';
import { t } from '../../i18n/index.js';

/**
 * The build is too old to punch with.
 *
 * It replaces the punch button and nothing else. Hours, weekly off and payment advices stay where
 * they were: somebody who cannot update on the spot — no signal, a full phone, a borrowed handset
 * — should not also lose the ability to see what they are owed.
 *
 * The rule this screen exists to keep: never a dead end. There is always the update, always a
 * retry, and always a person to call. When the check itself could not reach the server it says
 * so, because "your app is out of date" and "we could not check just now" call for different
 * things from the worker, and only one of them is their problem to solve.
 */
export function UpdateRequired({
  state,
  onRecheck,
  checking,
}: {
  state: VersionState;
  onRecheck: () => void;
  checking: boolean;
}) {
  const needed = state.info?.minimum_version_code ?? null;
  const latest = state.info?.latest ?? null;
  const phone = state.info?.help_phone ?? null;

  return (
    <section className="card update-required" aria-live="polite">
      <h2>{t('too_old_title')}</h2>
      <p>{t('too_old_body')}</p>
      {state.mine !== null && needed !== null && (
        <p className="muted small-text">
          {t('too_old_builds', {
            mine: String(state.mine),
            needed: String(needed),
            ...(latest ? { name: latest.version_name } : { name: '' }),
          })}
        </p>
      )}

      {/* The check itself failed. Said plainly, because it changes what the worker should do. */}
      {state.stale && <p className="warn-text">{t('too_old_stale')}</p>}

      <div className="row">
        {state.updateUrl && (
          <a className="button" href={state.updateUrl} rel="noreferrer">
            <Download size={16} aria-hidden="true" />{' '}
            {state.fromPlay ? t('update_open_play') : t('update_download')}
          </a>
        )}
        <button type="button" className="secondary" onClick={onRecheck} disabled={checking}>
          <RefreshCw size={16} aria-hidden="true" />{' '}
          {checking ? t('too_old_checking') : t('too_old_recheck')}
        </button>
      </div>

      {!state.updateUrl && <p className="muted">{t('too_old_no_link')}</p>}

      {phone && (
        <p className="muted help-line">
          <Phone size={16} aria-hidden="true" />{' '}
          {t('too_old_call')}{' '}
          <a href={`tel:${phone.replace(/\s+/g, '')}`}>{fmtPhone(phone)}</a>
        </p>
      )}
      <p className="muted small-text">{t('too_old_rest_works')}</p>
    </section>
  );
}
